import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import TaskTypeSelectionModal from './TaskTypeSelectionModal';
import ProblemTaskModal from './ProblemTaskModal';
import {
  problemTaskStateLabels,
  problemTaskTypeLabels,
  problemTaskAssignedToLabels,
  getDisplayLabel
} from '../utils/labelMappings';
import { stripHtml as sanitizeStripHtml } from '../utils/sanitize';

// Helper function to strip HTML tags for plain text display
// SECURITY: Uses DOMPurify-based stripHtml for safe HTML removal
const stripHtml = (html: string | null): string => {
  if (!html) return '-';
  const result = sanitizeStripHtml(html);
  return result || '-';
};

interface ProblemTasksTabProps {
  problemId: string;
  problemNumber: string;
  engagementId: string | null;
  hideNewButton?: boolean;
  customerId?: string | null;
}

interface ProblemTask {
  id: string;
  number: string;
  problemId: string;
  type: string;
  assignedTo: string;
  resourceId: string | null;
  assignmentGroupId: string | null;
  assignedToId: string | null;
  state: string;
  description: string | null;
  serviceImprovements: string | null;
  createdAt: string;
  updatedAt: string;
  createdById: string;
  updatedById: string | null;
  Resource?: {
    id: string;
    name: string;
    resourceType: string;
  } | null;
  AssignmentGroup?: {
    id: string;
    name: string;
    description: string | null;
  } | null;
  AssignedToUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  User_ProblemTask_createdByIdToUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  User_ProblemTask_updatedByIdToUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

const TASKS_PER_PAGE = 10;

const ProblemTasksTab: React.FC<ProblemTasksTabProps> = ({
  problemId,
  problemNumber,
  engagementId,
  hideNewButton = false,
  customerId
}) => {
  const [typeSelectionModalOpen, setTypeSelectionModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedTaskType, setSelectedTaskType] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<ProblemTask | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch problem tasks
  const {
    data: tasks,
    isLoading,
    error,
    refetch
  } = useQuery<ProblemTask[]>({
    queryKey: ['problem-tasks', problemId],
    queryFn: async () => {
      const response = await api.get(`/cases/${problemId}/problem-tasks`);
      return response.data;
    }
  });

  const handleNewTaskClick = () => {
    setSelectedTask(null);
    setTypeSelectionModalOpen(true);
  };

  const handleTypeSelected = (type: string) => {
    setSelectedTaskType(type);
    setTypeSelectionModalOpen(false);
    setTaskModalOpen(true);
  };

  const handleTaskClick = (task: ProblemTask) => {
    setSelectedTask(task);
    setSelectedTaskType(task.type);
    setTaskModalOpen(true);
  };

  const handleTaskModalClose = () => {
    setTaskModalOpen(false);
    setSelectedTask(null);
    setSelectedTaskType(null);
  };

  const handleTaskSuccess = (shouldClose: boolean = false) => {
    // Close the modal first if requested - do this synchronously
    if (shouldClose) {
      // Close the modal immediately
      setTaskModalOpen(false);
      setSelectedTask(null);
      setSelectedTaskType(null);
    }

    // Refetch tasks in the background (don't block on this)
    refetch()
      .then(({ data: updatedTasks }) => {
        // Only update selectedTask if modal is still open
        if (!shouldClose && selectedTask && updatedTasks) {
          const updatedTask = updatedTasks.find((t: ProblemTask) => t.id === selectedTask.id);
          if (updatedTask) {
            setSelectedTask(updatedTask);
          }
        }
      })
      .catch((error) => {
        console.error('Error refetching tasks:', error);
      });
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'new':
        return 'bg-blue-100 text-blue-700';
      case 'in_progress':
        return 'bg-blue-200 text-blue-800';
      case 'closed':
        return 'bg-blue-300 text-blue-900';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-gray-500">Loading problem tasks...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-red-500">Error loading problem tasks. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with New Task button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Problem Tasks</h3>
        {!hideNewButton && (
          <button
            onClick={handleNewTaskClick}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              className="-ml-1 mr-2 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            New Task
          </button>
        )}
      </div>

      {/* Task List Table */}
      {tasks && tasks.length > 0 ? (
        <>
          <div className="overflow-auto max-h-[500px] border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                  >
                    Task Number
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                  >
                    State
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                  >
                    Description
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                  >
                    Assigned Group
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                  >
                    Assigned To
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                  >
                    Owner
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                  >
                    Resource
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tasks
                  .slice((currentPage - 1) * TASKS_PER_PAGE, currentPage * TASKS_PER_PAGE)
                  .map((task) => (
                  <tr
                    key={task.id}
                    onClick={() => handleTaskClick(task)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">
                      {task.number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStateColor(
                          task.state
                        )}`}
                      >
                        {getDisplayLabel(task.state, problemTaskStateLabels)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="max-w-xs truncate">
                        {stripHtml(task.description)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getDisplayLabel(task.type, problemTaskTypeLabels)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {task.AssignmentGroup?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {task.AssignedToUser
                        ? `${task.AssignedToUser.firstName} ${task.AssignedToUser.lastName}`
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getDisplayLabel(task.assignedTo, problemTaskAssignedToLabels)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {task.Resource?.name || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {tasks.length > TASKS_PER_PAGE && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4 rounded-lg">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(Math.ceil(tasks.length / TASKS_PER_PAGE), p + 1))}
                  disabled={currentPage >= Math.ceil(tasks.length / TASKS_PER_PAGE)}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{(currentPage - 1) * TASKS_PER_PAGE + 1}</span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * TASKS_PER_PAGE, tasks.length)}
                    </span>{' '}
                    of <span className="font-medium">{tasks.length}</span> tasks
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {Array.from({ length: Math.ceil(tasks.length / TASKS_PER_PAGE) }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                          page === currentPage
                            ? 'z-10 bg-indigo-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(Math.ceil(tasks.length / TASKS_PER_PAGE), p + 1))}
                      disabled={currentPage >= Math.ceil(tasks.length / TASKS_PER_PAGE)}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No problem tasks</h3>
          <p className="mt-1 text-sm text-gray-500">
            {hideNewButton ? 'No problem tasks available.' : 'Get started by creating a new problem task.'}
          </p>
          {!hideNewButton && (
            <div className="mt-6">
              <button
                onClick={handleNewTaskClick}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg
                  className="-ml-1 mr-2 h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
                New Task
              </button>
            </div>
          )}
        </div>
      )}

      {/* Task Type Selection Modal */}
      <TaskTypeSelectionModal
        isOpen={typeSelectionModalOpen}
        onClose={() => setTypeSelectionModalOpen(false)}
        onTypeSelected={handleTypeSelected}
      />

      {/* Problem Task Modal */}
      {selectedTaskType && (
        <ProblemTaskModal
          isOpen={taskModalOpen}
          onClose={handleTaskModalClose}
          onSuccess={handleTaskSuccess}
          problemId={problemId}
          problemNumber={problemNumber}
          engagementId={engagementId}
          taskType={selectedTaskType}
          existingTask={selectedTask}
          customerId={customerId}
        />
      )}
    </div>
  );
};

export default ProblemTasksTab;
